import React, { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useLocation, Link } from 'react-router-dom';
import { ApplicationContext } from '../../context/GlobalState';
import { t, extractLocaleFromPath, localizedPath } from '../../i18n';
import HreflangTags from '../../components/HreflangTags/HreflangTags';
import doctorsData from '../../data/team/doctors.json';
import altTextData from '../../data/images/alt-text.json';
import './Team.css';

const APEX_BRANDS = ['apex-nc', 'apex-tn'];
const APEX_TEAM_HERO = '/assets/images/heroes/apex-meet-our-team.jpg';

function getTeamHero(brandKey) {
  return APEX_BRANDS.includes(brandKey) ? APEX_TEAM_HERO : null;
}

function getTeamHeroAlt(brandKey, locale) {
  if (APEX_BRANDS.includes(brandKey)) {
    const entry = altTextData['/assets/images/heroes/apex-meet-our-team.jpg'];
    return entry?.[locale] || entry?.en || 'Meet our team';
  }
  return 'Meet our team';
}

function getSiteKey(brandKey) {
  if (brandKey === 'gso') return 'gso';
  if (brandKey === 'apex-nc' || brandKey === 'apex-tn') return 'apex';
  return null;
}

export default function Team() {
  const { doctorSlug } = useParams();
  const location = useLocation();
  const { state } = useContext(ApplicationContext);
  const { locale } = extractLocaleFromPath(location.pathname);
  const brand = state.brand;
  const brandName = brand?.name || state.client?.name || 'Our Practice';
  const baseDomain = brand?.domain || state.client?.domain || 'gaspineortho.com';
  const brandKey = brand?.brandKey || '';

  const siteKey = getSiteKey(brandKey);
  const brandState = brand?.state || null;
  // State-based filter for apex brands (NC vs TN); legacy site filter for GSO.
  // Falls back to legacy `sites`/`site` when a provider lacks `states`.
  const filteredDoctors = (() => {
    if (brandKey === 'apex-nc' || brandKey === 'apex-tn') {
      return doctorsData.filter(doc => {
        if (Array.isArray(doc.states)) return doc.states.includes(brandState);
        if (Array.isArray(doc.sites)) return doc.sites.includes('apex');
        return doc.site === 'apex';
      });
    }
    if (siteKey) {
      return doctorsData.filter(doc => {
        if (Array.isArray(doc.sites)) return doc.sites.includes(siteKey);
        return doc.site === siteKey;
      });
    }
    return doctorsData;
  })();

  // Detail view
  if (doctorSlug) {
    // Provider profile pages render on any brand (cross-state SEO permissive).
    const doc = doctorsData.find(d => d.id === doctorSlug);

    if (!doc) {
      return (
        <div className="team-page">
          <div className="container" style={{ padding: '80px 20px', textAlign: 'center' }}>
            <p>{t('team.notFound', locale)}</p>
            <Link
              to={localizedPath('/meet-our-team', locale)}
              className="glass-btn glass-btn-secondary"
              style={{ display: 'inline-block', marginTop: 24 }}
            >
              {t('team.backToTeam', locale)}
            </Link>
          </div>
        </div>
      );
    }

    const content = doc.translations[locale] || doc.translations.en;
    const pageTitle = `${doc.name} | ${brandName}`;
    const pageDesc = `${doc.name} — ${content.title}. ${content.board}. ${t('team.metaDesc', locale, { brandName })}`;
    const canonicalUrl = `https://${baseDomain}/meet-our-team/${doc.id}`;
    const ogImage = doc.photo;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Physician',
      name: doc.name,
      description: content.board,
      medicalSpecialty: content.specialty,
      url: canonicalUrl,
      image: `https://${baseDomain}${doc.photo}`,
      worksFor: {
        '@type': 'MedicalOrganization',
        name: brandName,
      },
    };

    return (
      <div className="team-page">
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDesc} />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDesc} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:type" content="profile" />
          <meta property="og:image" content={`https://${baseDomain}${ogImage}`} />
          <link rel="canonical" href={canonicalUrl} />
          <script type="application/ld+json">{JSON.stringify(schema)}</script>
        </Helmet>
        <HreflangTags baseDomain={baseDomain} pathWithoutLocale={`/meet-our-team/${doc.id}`} />

        <section className="team-hero glass-dark">
          <div className="container">
            <h1>{doc.name}</h1>
            <p>{content.title}</p>
          </div>
        </section>

        <section className="doctor-detail section-padding">
          <div className="container">
            <div className="doctor-detail-card glass-card">
              <div className="doctor-detail-photo-wrapper">
                <img
                  src={doc.photo}
                  srcSet={doc.srcset}
                  sizes="(max-width: 768px) 150px, 300px"
                  alt={`${doc.name} - ${content.title}`}
                  className="doctor-detail-photo"
                  loading="lazy"
                />
              </div>
              <div className="doctor-detail-info">
                <h2>{doc.name}</h2>
                <p className="doctor-title">{content.title}</p>
                <p className="doctor-specialty">{content.specialty}</p>
                <p className="doctor-board">{content.board}</p>
                {content.bio && (
                  <div className="doctor-bio">
                    {content.bio.split(/\n\n+/).map((para, i) => (
                      <p key={i}>{para}</p>
                    ))}
                  </div>
                )}
                <Link
                  to={`${localizedPath('/request-appt', locale)}?provider=${encodeURIComponent(doc.id)}`}
                  className="glass-btn glass-btn-primary"
                >
                  {t('team.cta', locale)}
                </Link>
                <Link
                  to={localizedPath('/meet-our-team', locale)}
                  className="glass-btn glass-btn-secondary"
                  style={{ marginLeft: 12 }}
                >
                  {t('team.backToTeam', locale)}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // List view
  const pageTitle = t('team.pageTitle', locale, { brandName });
  const pageDesc = t('team.metaDesc', locale, { brandName });
  const canonicalUrl = `https://${baseDomain}/meet-our-team`;
  const ogImage = `https://${baseDomain}/assets/brand/${brandKey}/logo-color.webp`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalOrganization',
    name: brandName,
    url: canonicalUrl,
    member: filteredDoctors.map(doc => {
      const content = doc.translations[locale] || doc.translations.en;
      return {
        '@type': 'Physician',
        name: doc.name,
        medicalSpecialty: content.specialty,
        description: content.board,
        image: `https://${baseDomain}${doc.photo}`,
        url: `https://${baseDomain}/meet-our-team/${doc.id}`,
      };
    }),
  };

  return (
    <div className="team-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={ogImage} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <HreflangTags baseDomain={baseDomain} pathWithoutLocale="/meet-our-team" />

      <section
        className="team-hero glass-dark"
        style={getTeamHero(brandKey) ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${getTeamHero(brandKey)})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
      >
        <div className="container">
          <h1>{t('team.heroTitle', locale, { brandName })}</h1>
          <p>{t('team.heroSubtitle', locale, { brandName })}</p>
        </div>
      </section>

      <section className="team-grid section-padding">
        <div className="container">
          <div className="doctors-grid">
            {filteredDoctors.map(doc => {
              const content = doc.translations[locale] || doc.translations.en;
              return (
                <Link
                  key={doc.id}
                  to={localizedPath(`/meet-our-team/${doc.id}`, locale)}
                  className={`doctor-card glass-card${doc.id === 'erik-bendiks' ? ' owner-card' : ''}`}
                >
                  <div className="doctor-photo-wrapper">
                    <img
                      src={doc.photo}
                      srcSet={doc.srcset}
                      sizes="(max-width: 768px) 150px, 300px"
                      alt={`${doc.name} - ${content.title}`}
                      className="doctor-photo-img"
                      loading="lazy"
                    />
                  </div>
                  <h3>{doc.name}</h3>
                  <p className="doctor-title">{content.title}</p>
                  <p className="doctor-specialty-small">{content.specialty}</p>
                  <span className="doctor-view-profile">{t('team.viewProfile', locale)}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
